import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import AnimatedTableChevron from 'src/components/AnimatedTableChevron/AnimatedTableChevron'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ObjectTable from 'src/components/ObjectTable/ObjectTable'
import ObjectTableBodyCell from 'src/components/ObjectTableBodyCell/ObjectTableBodyCell'
import ObjectTableBodyRow from 'src/components/ObjectTableBodyRow/ObjectTableBodyRow'
import ObjectTableHeaderRow from 'src/components/ObjectTableHeaderRow/ObjectTableHeaderRow'
import SortableTableHeader, {
  SortableTableHeaderAlignment,
} from 'src/components/SortableTableHeader/SortableTableHeader'
import SvgArrowForward from 'src/components/Svgs/SvgArrowForward'
import TeamAvatarsIcon from 'src/components/TeamAvatarsIcon/TeamAvatarsIcon'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import {
  OrganizationInviteModel,
  OrganizationsListResponse,
} from 'src/models/organizations.models'
import { budgetOverviewUrl, manageOrgInviteUrl } from 'src/navigation/urls'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectOrganizationInvitesModelsFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectCreditCardsSourceCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { css } from 'styled-components'

interface BudgetsTableLargeProps {
  budgets: CreditCardListModel[]
}

const BudgetsTableLarge: React.FC<BudgetsTableLargeProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const currencyFormatter = useCurrencyFormatter()
  const users = useSelector(selectUserListModelsFromState)
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const creditCardsSourceCount = useSelector(
    selectCreditCardsSourceCountStatisticsFromState,
  )
  const currentUser = useCurrentUser()
  const orgInvites = useSelector(selectOrganizationInvitesModelsFromState)

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    dispatchStatistics([appStatisticsKeys.CreditCardsSourceCountStatistics])
  }, [dispatchStatistics])

  return !statisticsLoaded.creditCardsSourceCountStatisticsLoaded ? (
    <LoadingBackgroundPage />
  ) : (
    <ObjectTable>
      <ObjectTableHeaderRow>
        <SortableTableHeader
          css={css`
            width: 24%;

            ${getBreakpointDown(breakpointSizes.MD)(css`
              width: 25%;
            `)};
          `}
          alignment={SortableTableHeaderAlignment.LeftAligned}
          label={translate('Budget Name')}
        />
        {creditCardsSourceCount > 0 && (
          <SortableTableHeader
            css={css`
              width: 24%;

              ${getBreakpointDown(breakpointSizes.MD)(css`
                width: 25%;
              `)};
            `}
            alignment={SortableTableHeaderAlignment.LeftAligned}
            label={translate('Budget User')}
          />
        )}
        <SortableTableHeader
          css={css`
            width: 24%;

            ${getBreakpointDown(breakpointSizes.MD)(css`
              width: 25%;
            `)};
          `}
          alignment={SortableTableHeaderAlignment.LeftAligned}
          label={translate('Budget Creator')}
        />
        <SortableTableHeader
          css={css`
            width: 12%;

            ${getBreakpointDown(breakpointSizes.MD)(css`
              width: 0%;
              overflow: hidden;
              pointer-events: none;
            `)};
          `}
          alignment={SortableTableHeaderAlignment.RightAligned}
          label={translate('Exposure')}
        />
        <SortableTableHeader
          css={css`
            width: 12%;

            ${getBreakpointDown(breakpointSizes.MD)(css`
              width: 21%;
            `)};
          `}
          alignment={SortableTableHeaderAlignment.RightAligned}
          label={translate('Remaining')}
        />
        <SortableTableHeader
          css={css`
            width: 4%;
          `}
          alignment={SortableTableHeaderAlignment.RightAligned}
        />
      </ObjectTableHeaderRow>
      {props.budgets.map((budget, i) => {
        const canViewBudget =
          budget.organizationId === currentUser?.organizationId ||
          budget?.ownerId === currentUser?.id

        let budgetAndInviteOrg: OrganizationsListResponse | undefined
        let correspondingInviteId
        Object.values(orgInvites).forEach((invite: OrganizationInviteModel) => {
          if (invite.organization.id === budget.organizationId) {
            budgetAndInviteOrg = invite.organization
            correspondingInviteId = invite.id
          }
        })

        return (
          <ObjectTableBodyRow
            to={budgetOverviewUrl(budget.id)}
            key={i}
            ariaLabelSource={'Account Overview'}
          >
            <ObjectTableBodyCell
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
                overflow: visible;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                <img
                  css={css`
                    height: 20px;
                  `}
                  src={budget.cardTypeImageSrc}
                  alt={''}
                />
                <span
                  css={css`
                    margin-left: 6px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  `}
                >
                  {budget.truncatedDisplayName}
                </span>
              </div>
              {!canViewBudget && (
                <div
                  css={css`
                    width: 333%;
                    display: flex;
                    flex-direction: column;
                    border-radius: 10px;
                    border: 1px solid ${styleConstants.colors.red_dark};
                    background-color: ${styleConstants.colors.red_light};
                    margin-top: 18px;
                    padding: 12px 20px;
                    ${getFontCss(
                      14,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                    cursor: default;
                  `}
                  onClick={(e): void => {
                    e.preventDefault()
                  }}
                  onKeyPress={(e): void => {
                    e.preventDefault()
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        styleConstants.colors.red_dark,
                        styleConstants.fontFamilies.medium,
                      )};
                      margin-bottom: 12px;
                    `}
                  >
                    {translate('Action Required')}
                  </span>
                  {translate(
                    'To access the budget, you need to be a part of the org. ${cta}',
                    {
                      cta:
                        budgetAndInviteOrg !== undefined
                          ? translate(
                              'Accept your invite to join ${orgName} to access the budget and the permissions of a budget user.',
                              { orgName: budgetAndInviteOrg.name },
                            )
                          : translate(
                              'Please contact the Owner or Admin to get an invitation.',
                            ),
                    },
                  )}
                  {correspondingInviteId !== undefined && (
                    <HoverableLink
                      to={manageOrgInviteUrl(correspondingInviteId)}
                      ariaLabelSource={'Review the Invite'}
                      size={HoverableLinkSizes.Large}
                      underline
                      css={css`
                        width: fit-content;
                      `}
                    >
                      <span
                        css={css`
                          display: flex;
                          align-items: center;
                          ${getFontCss(
                            14,
                            themeConstants.colors.cta,
                            styleConstants.fontFamilies.regular,
                          )};
                          margin-top: 8px;
                        `}
                      >
                        {translate('Review the invite')}
                        <SvgArrowForward
                          css={css`
                            transform: scale(calc(16 / 24));
                          `}
                        />
                      </span>
                    </HoverableLink>
                  )}
                </div>
              )}
            </ObjectTableBodyCell>
            {creditCardsSourceCount > 0 && (
              <ObjectTableBodyCell
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {budget.budgetUsers !== undefined &&
                budget.budgetUsers.length > 1 ? (
                  <TeamAvatarsIcon users={budget.budgetUsers} showPill />
                ) : (
                  <UserAvatarAndName user={users[budget.userId]} />
                )}
              </ObjectTableBodyCell>
            )}
            <ObjectTableBodyCell
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              <UserAvatarAndName user={users[budget.ownerId]} />
            </ObjectTableBodyCell>
            <ObjectTableBodyCell
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                text-align: right;

                ${getBreakpointDown(breakpointSizes.MD)(css`
                  width: 0%;
                  overflow: hidden;
                  pointer-events: none;
                `)};
              `}
            >
              {budget.unspentAmountLabel}
            </ObjectTableBodyCell>
            {!canViewBudget ? (
              <ObjectTableBodyCell
                css={css`
                  text-align: right;
                `}
              >
                {currencyFormatter({
                  amount: 0,
                  currency: budget.currency,
                  maximumFractionDigits: 0,
                }).replace('0', '********')}
              </ObjectTableBodyCell>
            ) : (
              <ObjectTableBodyCell
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )};
                  text-align: right;

                  ${(budget.isInactive || budget.isClosed) &&
                  css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.red,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                `}
              >
                {budget.unissuedAmountLabel}
              </ObjectTableBodyCell>
            )}
            <ObjectTableBodyCell
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                overflow: visible;
              `}
            >
              <AnimatedTableChevron
                css={css`
                  margin-top: -4px;
                `}
              />
            </ObjectTableBodyCell>
          </ObjectTableBodyRow>
        )
      })}
    </ObjectTable>
  )
}

export default BudgetsTableLarge
